import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import { PopperPlacementType } from "@mui/base/Popper";
import {
  areAllEuCountriesSelected,
  countries,
  EUROPEAN_UNION_CODE,
  EUROPEAN_UNION_LABEL,
  toggleEuCountries,
} from "@redotech/locale/countries";
import { genericMemo, IterableMap } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { Input } from "@redotech/ui/form";
import classNames from "classnames";
import {
  Fragment,
  Key,
  ReactElement,
  ReactNode,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";

import { getDatadogActionName } from "./datadog-action-name";
import { Dropdown, DropdownOption } from "./dropdown";
import CheckIcon from "./icon-old/check.svg";
import ChevronDownIcon from "./icon-old/chevron-down.svg";
import { LabeledInput, LabelSize, LabelTheme } from "./labeled-input";
import * as selectDropdownCss from "./select-dropdown.module.css";

export namespace SelectDropdownTheme {
  export const BRAND = Symbol("Brand");
  export const GHOST = Symbol("Ghost");
}

export type SelectDropdownTheme =
  | typeof SelectDropdownTheme.BRAND
  | typeof SelectDropdownTheme.GHOST;

export interface OptionConfig {
  id: string;
  name: string;
  Render?(): ReactElement;
}

export interface BaseDropdownProps<T> {
  children(option: T): ReactNode;
  keyFn?(option: T, index: number): Key;
  id?: string;
  options: readonly T[][] | readonly T[];
  className?: string;
  "data-target"?: string;
  "data-dd-action-name"?: string;
}

export interface SingleSelectDropdownProps<T> extends BaseDropdownProps<T> {
  value: T | undefined;
  valueChange(value: T | undefined): void;
  display?(option: T): ReactNode;
  closeOnSelect?: boolean;
  placeholder?: string;
  disabled?: boolean;
  allowNull?: boolean;
  showChevron?: boolean;
  icon?: ReactNode;
  actionButton?: ReactNode;
  theme?: SelectDropdownTheme;
}

export interface MultiSelectDropdownProps<T> extends BaseDropdownProps<T> {
  value: readonly T[] | undefined;
  valueChange(value: readonly T[] | undefined): void;
  display?(option: readonly T[]): ReactNode;
  closeOnSelect?: boolean;
  disabled?: boolean;
  flip?: boolean; // false prevents dropdowns from changing from down to up when there's not enough space
  placement?: PopperPlacementType;
  showChevron?: boolean;
  icon?: ReactNode;
  actionButton?: ReactNode;
  theme?: SelectDropdownTheme;
  checkElement?(option: T): ReactNode;
  reducedPadding?: boolean;
  fitToAnchor?: boolean;
  isOptionSelected?(option: T, index: number): boolean;
  onOptionToggle?(option: T, index: number, willBeSelected: boolean): void;
}

const themeClass = {
  [SelectDropdownTheme.BRAND]: selectDropdownCss.brand,
  [SelectDropdownTheme.GHOST]: selectDropdownCss.ghost,
};

export const SelectDropdown = genericMemo(function SelectDropdown<T>({
  children,
  value,
  valueChange,
  display,
  placeholder = "",
  disabled = false,
  allowNull = false,
  showChevron = true,
  icon,
  closeOnSelect = false,
  actionButton,
  theme = SelectDropdownTheme.BRAND,
  "data-target": dataTarget,
  ...props
}: SingleSelectDropdownProps<T>) {
  const [arrayValue, setArrayValue] = useState<[T?]>([]);

  useEffect(() => {
    if (value === undefined) {
      setArrayValue([]);
    } else {
      setArrayValue([value]);
    }
  }, [value]);

  const onChange = useHandler((newValue: T[]) => {
    if (newValue.length) {
      // The last value is the newest value
      valueChange(newValue[newValue.length - 1]);
    }
  });

  const singleDisplay = useHandler((option: T[]) => {
    if (allowNull) {
      if (option?.[0] !== undefined) {
        return display ? display(option[0]) : children(option[0]);
      }
    } else if (option?.[0] != null) {
      return display ? display(option[0]) : children(option[0]);
    }
    return placeholder;
  });

  return (
    <MultiSelectDropdown
      {...props}
      actionButton={actionButton}
      closeOnSelect
      data-target={dataTarget}
      disabled={disabled}
      display={singleDisplay}
      icon={icon}
      showChevron={showChevron}
      theme={theme}
      value={arrayValue}
      valueChange={onChange}
    >
      {children}
    </MultiSelectDropdown>
  );
});

export const MultiSelectDropdown = genericMemo(function MultiSelectDropdown<T>({
  children,
  display,
  keyFn,
  id, // TODO: implement
  options,
  value,
  valueChange,
  className,
  closeOnSelect = false,
  disabled = false,
  flip = true,
  placement = undefined,
  showChevron = true,
  icon,
  actionButton,
  theme = SelectDropdownTheme.BRAND,
  checkElement,
  reducedPadding = false,
  fitToAnchor = true,
  isOptionSelected,
  onOptionToggle,
  "data-dd-action-name": datadogActionName,
  "data-target": dataTarget,
}: MultiSelectDropdownProps<T>) {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [popover, setPopover] = useState<HTMLElement | null>(null);

  const [open, setOpen] = useState(false);

  const themeClassName = themeClass[theme];

  const onClickAway = useHandler((e: MouseEvent | TouchEvent) => {
    // Don't close if toggling an option in a multi-select dropdown
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    if (!closeOnSelect && popover?.contains(e.target as Node)) {
      return;
    }
    setOpen(false);
  });
  let label: ReactNode;
  if (display) {
    label = display(value || []);
  } else {
    label = value?.map((v, i) => (
      <Fragment key={keyFn ? keyFn(v, i) : i}>{children(v)}</Fragment>
    ));
  }

  const optionIsSelected = (option: T, optionIndex: number): boolean => {
    if (isOptionSelected) {
      return isOptionSelected(option, optionIndex);
    }
    if (keyFn) {
      const optionKey = keyFn(option, optionIndex);
      return value?.some((v, i) => keyFn(v, i) === optionKey) || false;
    } else {
      return value?.includes(option) || false;
    }
  };

  const getDropdownOption = (option: T, index: number) => {
    function action() {
      const isSelected = optionIsSelected(option, index);
      if (onOptionToggle) {
        onOptionToggle(option, index, !isSelected);
        if (closeOnSelect) {
          setOpen(false);
        }
        return;
      }
      if (isSelected) {
        const valueAfterRemoval = (value || []).filter((item, idx) => {
          if (keyFn) {
            return keyFn(item, idx) !== keyFn(option, index);
          } else {
            return item !== option;
          }
        });

        valueChange(valueAfterRemoval);
        if (closeOnSelect) {
          setOpen(false);
        }
      } else {
        valueChange([...(value || []), option]);
        if (closeOnSelect) {
          setOpen(false);
        }
      }
    }
    return (
      <DropdownOption
        action={action}
        data-dd-action-name={getDatadogActionName(datadogActionName, "select")}
        disabled={disabled}
      >
        <div>{children(option)}</div>
        {optionIsSelected(option, index) &&
          (checkElement ? (
            checkElement(option)
          ) : (
            <CheckIcon className={selectDropdownCss.check} />
          ))}
      </DropdownOption>
    );
  };

  return (
    <div
      className={classNames(selectDropdownCss.container, className)}
      data-target={dataTarget}
      data-web="MultiSelectDropdown"
      ref={setContainer}
    >
      <ClickAwayListener
        disableReactTree
        onClickAway={onClickAway}
        touchEvent={false}
      >
        <button
          className={classNames(selectDropdownCss.select, themeClassName, {
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            [selectDropdownCss.reducedPadding]: reducedPadding,
          })}
          data-dd-action-name={getDatadogActionName(
            datadogActionName,
            "menu_toggle",
          )}
          disabled={disabled}
          onClick={(e) => {
            e.preventDefault();
            setOpen((value) => !value);
          }}
          type="button"
        >
          {icon}
          {label}
          {showChevron && (
            <ChevronDownIcon className={selectDropdownCss.selectIcon} />
          )}
        </button>
      </ClickAwayListener>
      <Dropdown
        anchor={container}
        fitToAnchor={fitToAnchor}
        flip={flip}
        open={open}
        placement={placement}
        ref={setPopover}
      >
        <IterableMap
          items={
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            Array.isArray(options[0]) ? (options as T[][]) : [options as T[]]
          }
          keyFn={(_, index) => index}
        >
          {(options, index) => (
            <>
              <IterableMap items={options} keyFn={keyFn || ((_, idx) => idx)}>
                {getDropdownOption}
              </IterableMap>
              {Array.isArray(options[0]) && index < options.length - 1 && (
                <hr className={selectDropdownCss.line} />
              )}
            </>
          )}
        </IterableMap>
        {actionButton && actionButton}
      </Dropdown>
    </div>
  );
});

export const FormSelectDropdown = genericMemo(function FormSelectDropdown<T>({
  description,
  label,
  input,
  valueChange,
  disabled = false,
  labelTheme,
  labelSize,
  ...props
}: {
  description?: ReactNode | ReactNode[];
  label: string;
  input: Input<T>;
  valueChange?(value: T): void;
  disabled?: boolean;
  labelTheme?: LabelTheme;
  labelSize?: LabelSize;
} & Omit<SingleSelectDropdownProps<T>, "value" | "valueChange">) {
  const id = useId();
  const onChange = (value: T) => {
    input.setValue(value);
    if (valueChange) {
      valueChange(value);
    }
  };
  return (
    <LabeledInput
      description={description}
      errors={input.changed ? input.errors : []}
      id={id}
      label={label}
      size={labelSize}
      theme={labelTheme}
    >
      <SelectDropdown
        disabled={disabled}
        value={input.value}
        valueChange={onChange}
        {...props}
      />
    </LabeledInput>
  );
});

export const FormMultiSelectDropdown = genericMemo(
  function FormMultiSelectDropdown<T>({
    description,
    label,
    input,
    valueChange,
    labelTheme,
    checkElement,
    ...props
  }: {
    description?: ReactNode | ReactNode[];
    label: string;
    valueChange?(value: T[]): void;
    labelTheme?: LabelTheme;
    input: Input<T[]>;
  } & Omit<MultiSelectDropdownProps<T>, "value" | "valueChange">) {
    const id = useId();
    const onChange = (value: readonly T[] | undefined) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const next = (value || []) as T[];
      input.setValue(next);
      if (valueChange) {
        valueChange(next);
      }
    };
    return (
      <LabeledInput
        description={description}
        errors={input.changed ? input.errors : []}
        id={id}
        label={label}
        theme={labelTheme}
      >
        <MultiSelectDropdown
          checkElement={checkElement}
          value={input.value}
          valueChange={onChange}
          {...props}
        />
      </LabeledInput>
    );
  },
);

export interface CountryFormMultiSelectDropdownProps<
  T extends string = string,
> extends Omit<
  MultiSelectDropdownProps<string>,
  | "value"
  | "valueChange"
  | "options"
  | "children"
  | "isOptionSelected"
  | "onOptionToggle"
> {
  description?: ReactNode | ReactNode[];
  label: string;
  valueChange?(value: T[]): void;
  labelTheme?: LabelTheme;
  input: Input<T[]>;
  emptyLabel?: string;
  options?: readonly string[];
  children?(option: string): ReactNode;
}

export const CountryFormMultiSelectDropdown = genericMemo(
  function CountryFormMultiSelectDropdown<T extends string = string>({
    description,
    label,
    input,
    valueChange,
    labelTheme,
    checkElement,
    emptyLabel = "All",
    display,
    options,
    children,
    ...props
  }: CountryFormMultiSelectDropdownProps<T>) {
    const id = useId();
    const defaultOptions = useMemo(
      () => [
        "US",
        EUROPEAN_UNION_CODE,
        ...countries.map((c) => c.code).filter((code) => code !== "US"),
      ],
      [],
    );
    const dropdownOptions = useMemo(() => {
      if (!options) {
        return defaultOptions;
      }
      if (options.includes(EUROPEAN_UNION_CODE)) {
        return options;
      }
      return [EUROPEAN_UNION_CODE, ...options];
    }, [defaultOptions, options]);

    const onChange = (value: readonly string[] | undefined) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const next = (value || []) as T[];
      input.setValue(next);
      if (valueChange) {
        valueChange(next);
      }
    };

    const isOptionSelected = (option: string) => {
      if (option === EUROPEAN_UNION_CODE) {
        return areAllEuCountriesSelected(input.value || []);
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      return input.value?.includes(option as T) || false;
    };

    const onOptionToggle = (
      option: string,
      _index: number,
      willBeSelected: boolean,
    ) => {
      let newValue: string[];
      if (option === EUROPEAN_UNION_CODE) {
        newValue = toggleEuCountries(input.value || [], willBeSelected);
      } else {
        const current = input.value || [];
        if (willBeSelected) {
          newValue = [...current, option];
        } else {
          newValue = current.filter((c) => c !== option);
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      input.setValue(newValue as T[]);
      if (valueChange) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        valueChange(newValue as T[]);
      }
    };

    const defaultDisplay = (value: readonly string[]) => {
      if (value.length === 0 || value.length === countries.length) {
        return emptyLabel;
      }
      const names = value.map(
        (code) =>
          countries.find((country) => country.code === code)?.name || code,
      );
      return names.join(", ");
    };

    const renderOption = (option: string) => {
      if (option === EUROPEAN_UNION_CODE) {
        return EUROPEAN_UNION_LABEL;
      }
      if (children) {
        return children(option);
      }
      return (
        countries.find((country) => country.code === option)?.name || option
      );
    };

    return (
      <LabeledInput
        description={description}
        errors={input.changed ? input.errors : []}
        id={id}
        label={label}
        theme={labelTheme}
      >
        <MultiSelectDropdown
          checkElement={checkElement}
          display={display ?? defaultDisplay}
          isOptionSelected={isOptionSelected}
          onOptionToggle={onOptionToggle}
          options={dropdownOptions}
          value={input.value}
          valueChange={onChange}
          {...props}
        >
          {renderOption}
        </MultiSelectDropdown>
      </LabeledInput>
    );
  },
);
